import { Empty, Scrollable, Spacer } from 'components/atoms';
import { CatergoriesCON } from 'pages/GoodsByCategories/style';
import { ProductSkeletons } from 'pages/Products';
import useVendorsByCategoriesHook from 'pages/VendorsByCategories/useVendorsByCategoriesHook';
import VendorsGrid from 'pages/VendorsByCategories/VendorsGrid';
import React, { useMemo } from 'react';

interface VendorsCategoriesWithFiltersProps {
  sort: string;
  filters: {
    bestsellers: boolean;
    promotions: boolean;
  };
}

const VendorsCategoriesWithFilters: React.FC<VendorsCategoriesWithFiltersProps> = ({ sort, filters }) => {
  const { fetchMoreCategoryVendors, categoryVendorsData, categoryVendorsFetching } = useVendorsByCategoriesHook();

  // Filter out any undefined or invalid vendor objects and apply sorting/filtering
  const processedVendors = useMemo(() => {
    const vendors = (categoryVendorsData?.pages?.flat() ?? []).filter(vendor => vendor?.seller?.id);

    if (vendors.length === 0) return [];

    let processed = [...vendors];

    // Apply filters
    if (filters.bestsellers) {
      // For now, all vendors are considered best sellers
      // processed = processed.filter(
      //   vendor => vendor?.seller?.verified || vendor?.seller?.business_approval === 'APPROVED'
      // );
    }

    if (filters.promotions) {
      processed = processed.filter(vendor => vendor?.hasPromotions || vendor?.discountPercentage > 0);
    }

    // Apply sorting
    switch (sort) {
      case 'popularity':
        processed.sort((a, b) => {
          const aPopularity = (a?.seller?.verified ? 10 : 0) + (a?.rating || 0);
          const bPopularity = (b?.seller?.verified ? 10 : 0) + (b?.rating || 0);
          return bPopularity - aPopularity;
        });
        break;
      case 'low-to-high':
        processed.sort((a, b) => (a?.averagePrice || 0) - (b?.averagePrice || 0));
        break;
      case 'high-to-low':
        processed.sort((a, b) => (b?.averagePrice || 0) - (a?.averagePrice || 0));
        break;
      case 'newest-arrivals':
        processed.sort((a, b) => {
          const aDate = new Date(a?.seller?.createdAt || 0).getTime();
          const bDate = new Date(b?.seller?.createdAt || 0).getTime();
          return bDate - aDate;
        });
        break;
      case 'discounted':
        processed.sort((a, b) => (b?.discountPercentage || 0) - (a?.discountPercentage || 0));
        break;
      case 'product-rating':
      default:
        processed.sort((a, b) => (b?.rating || 0) - (a?.rating || 0));
        break;
    }

    return processed;
  }, [categoryVendorsData?.pages, sort, filters]);

  // Show loader above filters and grid if loading and no data yet
  const isInitialLoading =
    categoryVendorsFetching && (!categoryVendorsData || categoryVendorsData?.pages?.flat()?.length === 0);

  return (
    <CatergoriesCON>
      {isInitialLoading ? (
        <div className="grid bg-white rounded-md grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <ProductSkeletons count={12} />
        </div>
      ) : (
        <>
          {(processedVendors ?? []).length > 0 && (
            <Scrollable onScrollBottom={fetchMoreCategoryVendors}>
              <VendorsGrid
                vendors={processedVendors}
                diva={processedVendors}
                isLoading={categoryVendorsFetching}
                emptyText="No vendors in this category"
              />
            </Scrollable>
          )}

          {processedVendors.length === 0 && !categoryVendorsFetching && (
            <Empty message="Oops! There are no vendors available in this category at the moment. Please check back later or explore other categories." />
          )}
        </>
      )}
      <Spacer height={'2.5rem'} />
    </CatergoriesCON>
  );
};

export default VendorsCategoriesWithFilters;
